import React, { useState } from "react";
import Dialog from "../../base-components/Headless/Dialog";
import Button from "../../base-components/Button";
import Lucide from "../../base-components/Lucide";
import { useNotificationPermissions } from "../../hooks/useNotificationPermissions";

interface NotificationPermissionModalProps {
  onEnablePermissions?: () => void;
}

const NotificationPermissionModal: React.FC<
  NotificationPermissionModalProps
> = ({ onEnablePermissions }) => {
  const { isModalOpen, hideModal, requestPermission } =
    useNotificationPermissions();
  const [showInstructions, setShowInstructions] = useState(false);
  const [browserType, setBrowserType] = useState("");
  const [instructionSteps, setInstructionSteps] = useState<string[]>([]);

  const handleClose = () => {
    setShowInstructions(false);
    hideModal();
  };

  const handleEnablePermissions = async () => {
    if (onEnablePermissions) {
      onEnablePermissions();
      handleClose();
      return;
    }

    // First try to request permission programmatically
    const permission = await requestPermission();

    if (permission === "granted") {
      handleClose();
      return;
    }

    // If permission wasn't granted, try to open browser settings directly
    const opened = openBrowserSettingsPage();

    // If direct navigation failed, show instructions as fallback
    if (!opened) {
      openBrowserNotificationSettings();
    } else {
      // Close modal since we opened settings in new tab
      handleClose();
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;

    if (userAgent.includes("Edg/")) return "edge";
    if (userAgent.includes("Chrome/")) return "chrome";
    if (userAgent.includes("Firefox/")) return "firefox";
    if (userAgent.includes("Safari/") && !userAgent.includes("Chrome/"))
      return "safari";
    return "other";
  };

  const openBrowserSettingsPage = (): boolean => {
    const browser = getBrowserInfo();
    const hostname = window.location.hostname;

    try {
      switch (browser) {
        case "chrome":
          window.open(`chrome://settings/content/all`);
          return true;
        case "edge":
          window.open(`edge://settings/content/notifications`, "_blank");
          return true;
        case "firefox":
          // Firefox doesn't allow direct settings access, return false to show instructions
          return false;
        case "safari":
          // Safari doesn't allow direct settings access, return false to show instructions
          return false;
        default:
          return false;
      }
    } catch (error) {
      console.log("Failed to open browser settings directly:", error);
      return false;
    }
  };

  const openBrowserNotificationSettings = () => {
    const browser = getBrowserInfo();

    switch (browser) {
      case "chrome":
      case "edge":
        displayInstructions("Chrome/Edge", [
          "Option 1: Click the lock or info icon (🔒/ℹ️) in the address bar",
          'Select "Site Settings" or "Permissions"',
          'Find "Notifications" and change it to "Allow"',
          "Option 2: Copy this URL to your address bar: chrome://settings/content/",
          'Search for this website and set notifications to "Allow"',
          "Refresh the page to apply changes",
        ]);
        break;

      case "firefox":
        displayInstructions("Firefox", [
          "Click the shield or lock icon (🛡️/🔒) in the address bar",
          'Click on "Notifications" or "Permissions"',
          'Select "Allow" for this site',
          "Refresh the page to apply changes",
        ]);
        break;

      case "safari":
        displayInstructions("Safari", [
          "Go to Safari → Preferences (or Settings)",
          'Click the "Websites" tab',
          'Select "Notifications" from the left sidebar',
          'Find this website and set it to "Allow"',
          "Refresh the page to apply changes",
        ]);
        break;

      default:
        displayInstructions("Your Browser", [
          "Look for a lock (🔒) or info icon (ℹ️) in the address bar",
          "Click on it to open site permissions",
          'Find notification settings and set to "Allow"',
          "Refresh the page to apply changes",
        ]);
        break;
    }
  };

  const displayInstructions = (browserName: string, steps: string[]) => {
    setBrowserType(browserName);
    setInstructionSteps(steps);
    setShowInstructions(true);
  };

  return (
    <Dialog open={isModalOpen} onClose={handleClose}>
      <Dialog.Panel className="w-96 mt-36">
        <Dialog.Title>
          <Lucide icon="Bell" className="w-6 h-6 mr-3 text-yellow-500" />
          <h2 className="mr-auto text-base font-medium">
            Enable Notifications
          </h2>
        </Dialog.Title>
        <Dialog.Description>
          {!showInstructions ? (
            <div className="text-center">
              <div className="mb-4">
                <Lucide
                  icon="BellOff"
                  className="w-16 h-16 mx-auto text-gray-400 mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Stay Updated with Notifications
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                To receive important updates, reminders, call notifications, and
                real-time alerts, please enable notifications for this website.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <Lucide icon="Info" className="w-4 h-4 inline mr-1" />
                  We'll first try to request permission automatically, then show
                  you step-by-step instructions if needed.
                </p>
              </div>
            </div>
          ) : (
            <div className="px-2">
              <div className="text-center mb-4">
                <Lucide
                  icon="Monitor"
                  className="w-12 h-12 mx-auto text-blue-500 mb-2"
                />
                <h3 className="text-lg font-semibold mb-2">
                  Enable Notifications in {browserType}
                </h3>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3 font-medium">
                  <Lucide
                    icon="AlertTriangle"
                    className="w-4 h-4 inline mr-1"
                  />
                  Follow these steps to enable notifications:
                </p>
                <ol className="space-y-2">
                  {instructionSteps.map((step, index) => (
                    <li
                      key={index}
                      className="flex items-start text-sm text-yellow-800 dark:text-yellow-200"
                    >
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-yellow-200 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-200 rounded-full text-xs font-semibold mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 text-center">
                  <Lucide icon="CheckCircle" className="w-4 h-4 inline mr-1" />
                  After enabling notifications, refresh this page to complete
                  the setup!
                </p>
              </div>
            </div>
          )}
        </Dialog.Description>
        <Dialog.Footer>
          {!showInstructions ? (
            <>
              <div className="flex justify-end w-full">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleClose}
                  className="w-50 mr-2"
                >
                  Maybe Later
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleEnablePermissions}
                  className="w-50"
                >
                  <Lucide icon="Settings" className="w-4 h-4 mr-2" />
                  Enable Now
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => setShowInstructions(false)}
                className="w-24 mr-2"
              >
                <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => window.location.reload()}
                className="w-32"
              >
                <Lucide icon="RefreshCw" className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
            </>
          )}
        </Dialog.Footer>
      </Dialog.Panel>
    </Dialog>
  );
};

export default NotificationPermissionModal;
